import type {
  CreateSubSample,
  ExternalReference,
  ExternalReferenceType,
  Sample,
  SampleFormData,
  SamplePayload,
  SubSamplesFormData
} from 'prove-common'

export const LOKALITET_SAMPNT_TYPE_CODE = 'E152A' // Oppdrettsanlegg

export const getExternalReferenceType = (
  sampntTypeCode: string | undefined
): ExternalReferenceType => {
  return sampntTypeCode === LOKALITET_SAMPNT_TYPE_CODE
    ? 'LOKALITET'
    : 'PRODUSENT'
}

export const getExternalReference = (
  formData: SampleFormData,
  type: ExternalReferenceType
): ExternalReference => {
  if (type === 'LOKALITET') {
    return {
      externalReferenceId: formData.lokalitet?.externalReferenceId,
      organisasjonsnummer: formData.lokalitet?.organisasjonsnummer,
      virksomhetsnavn: formData.lokalitet?.virksomhetsnavn,
      tilsynsobjektId: undefined,
      aktivitet: undefined
    }
  }

  return {
    externalReferenceId: formData.produsent?.externalReferenceId,
    organisasjonsnummer: formData.produsent?.organisasjonsnummer,
    virksomhetsnavn: formData.produsent?.virksomhetsnavn,
    tilsynsobjektId: formData.produsent?.tilsynsobjektId,
    aktivitet: formData.produsent?.aktivitet
  }
}

const normalizeReinbeitedistrikt = (
  payload: Record<string, unknown>
): Record<string, unknown> => {
  const reinbeitedistrikt = payload.reinbeitedistrikt
  if (
    reinbeitedistrikt == null ||
    typeof reinbeitedistrikt !== 'object' ||
    Array.isArray(reinbeitedistrikt)
  ) {
    return payload
  }

  const district = reinbeitedistrikt as Record<string, unknown>
  return {
    ...payload,
    reinbeitedistrikt: { codevalue: district.codevalue, label: district.label }
  }
}

export const getPayloadWithSubSamples = (
  sample: Sample | undefined,
  payload: string | undefined
): Partial<SampleFormData> => {
  const referenceType = sample?.sampntType?.code
    ? getExternalReferenceType(sample.sampntType.code)
    : undefined

  const produsent =
    referenceType === 'PRODUSENT'
      ? ({
          produsent: {
            externalReferenceId: sample?.externalReferenceId,
            organisasjonsnummer: sample?.orgnr?.toString(),
            virksomhetsnavn: sample?.businessName,
            tilsynsobjektId: sample?.oversightObject,
            aktivitet: undefined
          }
        } satisfies Pick<SampleFormData, 'produsent'>)
      : {}

  const lokalitet =
    referenceType === 'LOKALITET'
      ? ({
          lokalitet: {
            externalReferenceId: sample?.externalReferenceId,
            organisasjonsnummer: sample?.orgnr?.toString(),
            virksomhetsnavn: undefined,
            tilsynsobjektId: undefined,
            aktivitet: undefined
          }
        } satisfies Pick<SampleFormData, 'lokalitet'>)
      : {}

  return {
    takenDate: new Date(sample?.takenDate ?? new Date())
      .toISOString()
      .split('T')[0],
    ...produsent,
    ...lokalitet,
    nameRedacted: sample?.nameRedacted ? 'Yes' : 'No',
    ...(sample?.comment && { comment: sample.comment }),
    payload: {
      ...(payload == null
        ? {}
        : normalizeReinbeitedistrikt(JSON.parse(payload) ?? {})),
      subSamples: sample?.subSamples
        ?.toSorted((a, b) => a.subSampleId - b.subSampleId)
        ?.map((subSample) => {
          return {
            subSampleId: subSample.subSampleId,
            payload: JSON.parse(subSample.payload ?? '{}')
          }
        })
    }
  } satisfies Partial<SampleFormData>
}

export const createSubSamples = (
  subSamples: SubSamplesFormData
): CreateSubSample[] => {
  return subSamples.map((subSample) => ({
    subSampleId: subSample.subSampleId,
    payload: JSON.stringify(subSample.payload)
  }))
}

export const createSubsamplesByNumber = (
  payload: SamplePayload
): CreateSubSample[] => {
  const count = Number(payload.subSamplesNumber) || 0

  const cleanPayload = {
    ...payload,
    subSamplesNumber: undefined,
    subSamples: undefined
  }

  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    subSampleId: i + 1,
    payload: JSON.stringify(cleanPayload)
  }))
}

export const createSamplePayload = (
  payload: SamplePayload,
  produsentId?: string
): string => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { subSamples, subSamplesNumber, ...rest } =
    normalizeReinbeitedistrikt(payload)
  return JSON.stringify({
    ...rest,
    produsentId
  })
}

export const isNameRedacted = (
  nameRedacted: string | undefined
): boolean | undefined => {
  if (nameRedacted === 'Yes') {
    return true
  } else if (nameRedacted === 'No') {
    return false
  } else {
    return undefined
  }
}

export const sortSamplesByTakenDate = (samples: Sample[]): Sample[] => {
  return samples.sort((a, b) => {
    if (a.takenDate == null || b.takenDate == null) {
      return -1
    } else {
      return new Date(b.takenDate).valueOf() - new Date(a.takenDate).valueOf()
    }
  })
}
